function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { withGenericConfig } from "/contexts/generic-config-context";
import { getAllInputFieldsNames, getInputDataByName, prePopulateInputsIfNeeded, inputValidate } from "client/services/form-service/form-service";
import Button from "components/core/button/button-component";
import PictureComponent from "components/core/picture/picture-component";
import GenericFormInput from "client/components/system/generic-form-input/generic-form-input";
import { PRIVACY_POLICY_LINK, HELP_CENTER_LINK } from "constants/links";
import { ConditionalWrapper } from "utils/react";
import { WORK_OS_IRIS_COLOR_NAME } from "styles/color-consts";
import Title from "segments/desktop/core-components/title/title";
import { XS } from "constants/sizes";
import RegularButton from "segments/desktop/core-components/button/regular-button/regular-button";
import * as Sentry from "@sentry/nextjs";
import { trackEvent } from "services/bigbrain-service";
import { CONTACT_SALES_FORM_SUBMISSION_CALL_FAILED } from "constants/bigbrain-event-types";
import { HUBSPOT_API_COMPANY_SIZE } from "client/services/hubspot-service/hubspot-constants";
import DefaultProps from "./default-props";
import { StyledLayeredBaseForm, StyledBaseForm } from "./base-form.scss";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { EMAIL_PATTERN } from "constants/input-validations-patterns";
import { inputTypes } from "client/services/form-service/form-inputs";
import { SMALLEST_COMPANY_SIZE } from "/client/services/form-service/form-constants";
import { withAbTests } from "contexts/ab-tests-context";
const DONE_ASSET_SRC = "/static/img/done_asset.png";
const MAX_BACKGROUND_LAYERS = 3;
const BACKGROUND_LAYER_MARGIN = 12;
const BaseForm = (props)=>{
    const { onInputBlur, onEmailChange, formConfig, isV2, translate, translateWithParam, submitScreenData, renderPrivacyNotice, submitFormAsyncCallback, dontRenderPostFormSubmit } = props;
    const [inputs, setInputs] = useState({});
    const [errorState, setErrorState] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [hasFormSubmitted, setHasFormSubmitted] = useState(false);
    const [formError, setFormError] = useState(false);
    const [formSubmitErrorMessage, setFormSubmitErrorMessage] = useState(null);
    const [backgroundLayers, setBackgroundLayers] = useState(props.backgroundLayers);
    const allInputsNames = useMemo(()=>{
        return getAllInputFieldsNames(formConfig);
    }, [
        formConfig
    ]);
    useEffect(()=>{
        const prePopulatedInputs = prePopulateInputsIfNeeded();
        if (Object.keys(prePopulatedInputs).length > 0) {
            setInputs(_object_spread({}, inputs, prePopulatedInputs));
        }
    }, []);
    const onInputChange = ({ target })=>{
        const { name, value } = target;
        setInputs((prevState)=>_object_spread_props(_object_spread({}, prevState), {
                [name]: value
            }));
        setErrorState((prevState)=>_object_spread_props(_object_spread({}, prevState), {
                [name]: {
                    isValid: true
                }
            }));
    };
    const onFormInputBlur = ({ target })=>{
        const { name, value } = target;
        if (onInputBlur) {
            onInputBlur(name, value);
        }
        if (name === inputTypes.EMAIL) {
            EMAIL_PATTERN.test(value) && onEmailChange(value);
        }
        validateInputOnBlur(name);
    };
    const focusOnFirstInvalidElement = ()=>{
        var _ariaInvalidInputs_;
        const ariaInvalidInputs = document.querySelectorAll('[aria-invalid="true"]');
        ariaInvalidInputs && ((_ariaInvalidInputs_ = ariaInvalidInputs[0]) === null || _ariaInvalidInputs_ === void 0 ? void 0 : _ariaInvalidInputs_.focus());
    };
    const validateInputOnBlur = (inputName)=>{
        const inputValue = inputs[inputName];
        const inputData = getInputDataByName(formConfig, inputName);
        const { isRequired, validate } = inputData;
        if (!isRequired && !validate) return;
        const { isValid, errorMessage } = inputValidate(inputData, inputValue);
        setErrorState(_object_spread_props(_object_spread({}, errorState), {
            [inputName]: {
                isValid,
                errorMessage
            }
        }));
    };
    const validateInputs = ()=>{
        const newErrorState = {};
        for (const inputName of allInputsNames){
            const inputValue = inputs[inputName];
            const inputData = getInputDataByName(formConfig, inputName);
            const { isValid, errorMessage } = inputValidate(inputData, inputValue, inputs);
            if (!isValid) newErrorState[inputName] = {
                isValid,
                errorMessage
            };
        }
        setErrorState(newErrorState);
        setTimeout(()=>{
            focusOnFirstInvalidElement();
        }, 0);
        return Object.keys(newErrorState).length > 0;
    };
    const renderInputList = ()=>{
        const { rows } = formConfig;
        return rows.map((row, rowIndex)=>{
            return /*#__PURE__*/ _jsx("div", {
                className: "contact-form-row",
                children: row.map((inputData)=>{
                    const { inputName, style } = inputData;
                    const value = inputs[inputName];
                    const hasError = errorState[inputName];
                    return /*#__PURE__*/ _jsx("div", {
                        className: "form-input-wrapper",
                        style: _object_spread({}, style),
                        children: /*#__PURE__*/ _jsx(GenericFormInput, {
                            inputData: inputData,
                            value: value,
                            errorState: hasError,
                            onInputChange: onInputChange,
                            onInputBlur: onFormInputBlur,
                            isV2: isV2
                        })
                    }, `input_${inputName}`);
                })
            }, `input_row_${rowIndex}`);
        });
    };
    const getHelpCenterLinkHTML = ()=>{
        return `<a href="${HELP_CENTER_LINK}" target="_blank">
      ${translate("forms.thanksPage.helpCenter")}</a>`;
    };
    const isSmallCompany = ()=>{
        var _inputs_HUBSPOT_API_COMPANY_SIZE;
        return ((_inputs_HUBSPOT_API_COMPANY_SIZE = inputs[HUBSPOT_API_COMPANY_SIZE]) === null || _inputs_HUBSPOT_API_COMPANY_SIZE === void 0 ? void 0 : _inputs_HUBSPOT_API_COMPANY_SIZE.value) === SMALLEST_COMPANY_SIZE;
    };
    const getSubmitScreenSubTitle = ()=>{
        const subTitleHtmlKey = submitScreenData === null || submitScreenData === void 0 ? void 0 : submitScreenData.subTitleHtmlKey;
        if (translate(subTitleHtmlKey)) {
            return translate(subTitleHtmlKey);
        }
        if (isSmallCompany()) {
            return translate("forms.thanksPage.smallCompany.subTitle");
        }
        return translate("forms.thanksPage.subTitle");
    };
    const getSubmitScreenData = ()=>{
        const footerLinkHtml = (submitScreenData === null || submitScreenData === void 0 ? void 0 : submitScreenData.footerLinkHtml) || getHelpCenterLinkHTML();
        return {
            postSubmitAssetSrc: (submitScreenData === null || submitScreenData === void 0 ? void 0 : submitScreenData.postSubmitAssetSrc) || DONE_ASSET_SRC,
            title: translate(submitScreenData === null || submitScreenData === void 0 ? void 0 : submitScreenData.titleKey) || translate("forms.thanksPage.title"),
            subTitleHtml: getSubmitScreenSubTitle(),
            footerHtml: translate(submitScreenData === null || submitScreenData === void 0 ? void 0 : submitScreenData.footerHtml) || translateWithParam("forms.thanksPage.footer", footerLinkHtml),
            downloadButtonLink: (submitScreenData === null || submitScreenData === void 0 ? void 0 : submitScreenData.downloadButtonLink) || false
        };
    };
    const renderPostFormSubmit = ()=>{
        const submitScreenData = getSubmitScreenData();
        const DownloadButtonText = translate("forms.thanksPage.gartnerCampaign.buttonText");
        return /*#__PURE__*/ _jsxs("div", {
            className: "contact-form-thanks",
            children: [
                /*#__PURE__*/ _jsx(PictureComponent, {
                    src: submitScreenData.postSubmitAssetSrc,
                    className: "submit-image",
                    isDecorative: true
                }),
                /*#__PURE__*/ _jsx("h2", {
                    className: "contact-form-thanks-title",
                    children: submitScreenData === null || submitScreenData === void 0 ? void 0 : submitScreenData.title
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "contact-form-thanks-content",
                    children: [
                        submitScreenData.subTitleHtml && /*#__PURE__*/ _jsx("span", {
                            dangerouslySetInnerHTML: {
                                __html: submitScreenData.subTitleHtml
                            }
                        }),
                        submitScreenData.footerHtml && /*#__PURE__*/ _jsx("span", {
                            className: "contact-form-thanks-footer",
                            dangerouslySetInnerHTML: {
                                __html: submitScreenData.footerHtml
                            }
                        }),
                        (submitScreenData === null || submitScreenData === void 0 ? void 0 : submitScreenData.downloadButtonLink) && /*#__PURE__*/ _jsx("div", {
                            className: "download-button-wrapper",
                            children: /*#__PURE__*/ _jsx(RegularButton, {
                                color: WORK_OS_IRIS_COLOR_NAME,
                                url: submitScreenData === null || submitScreenData === void 0 ? void 0 : submitScreenData.downloadButtonLink,
                                isOpenLinkInNewTab: true,
                                buttonText: DownloadButtonText
                            })
                        })
                    ]
                })
            ]
        });
    };
    const getPrivacyHtml = ()=>{
        const { privacyNoticeText } = formConfig;
        const privacyPolicyLink = `<a href="${PRIVACY_POLICY_LINK}" target="_blank">
      ${translate("forms.privacyPolicy")}</a>`;
        if (privacyNoticeText) return translateWithParam(privacyNoticeText, privacyPolicyLink);
        return translateWithParam("forms.privacyNoticeShorten", privacyPolicyLink);
    };
    const renderPrivacyNoticeIfNeeded = ()=>{
        if (!renderPrivacyNotice) {
            return null;
        }
        return /*#__PURE__*/ _jsx("div", {
            className: "privacy-notice-wrapper",
            children: /*#__PURE__*/ _jsx("span", {
                className: "privacy-notice",
                dangerouslySetInnerHTML: {
                    __html: getPrivacyHtml()
                }
            })
        });
    };
    const renderErrorIfNeeded = ()=>{
        if (!formError) return null;
        const errorMessage = formSubmitErrorMessage || "form.errors.formSubmitError";
        return /*#__PURE__*/ _jsx("div", {
            className: "error-wrapper",
            children: /*#__PURE__*/ _jsx("span", {
                className: "form-error",
                role: "alert",
                children: translate(errorMessage)
            })
        });
    };
    const renderForm = ()=>{
        const { submitButtonText, ctaProps, formTitle } = formConfig;
        const buttonText = translate((ctaProps === null || ctaProps === void 0 ? void 0 : ctaProps.overrideText) || submitButtonText || "forms.defaultSubmitButton");
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                formTitle && /*#__PURE__*/ _jsx("div", {
                    className: "form-title-wrapper",
                    children: /*#__PURE__*/ _jsx(Title, {
                        title: formTitle,
                        titleSize: XS
                    })
                }),
                renderInputList(),
                renderErrorIfNeeded(),
                renderPrivacyNoticeIfNeeded(),
                /*#__PURE__*/ _jsx(Button, {
                    color: (ctaProps === null || ctaProps === void 0 ? void 0 : ctaProps.color) || WORK_OS_IRIS_COLOR_NAME,
                    invert: ctaProps === null || ctaProps === void 0 ? void 0 : ctaProps.invert,
                    isLoading: isLoading,
                    onClickCallback: onButtonClick,
                    children: buttonText
                })
            ]
        });
    };
    const getFormData = ()=>{
        const formData = [];
        for(const input in inputs){
            formData.push({
                name: input,
                value: inputs[input]
            });
        }
        return formData;
    };
    const onButtonClick = function() {
        var _ref = _async_to_generator(function*() {
            const hasErrors = validateInputs();
            if (hasErrors) {
                return;
            }
            const formData = getFormData();
            setIsLoading(true);
            setFormError(false);
            setFormSubmitErrorMessage(null);
            const formResponse = yield submitFormAsyncCallback(formData);
            const urlToRedirectAfterSubmission = formResponse === null || formResponse === void 0 ? void 0 : formResponse.redirectUrl;
            if (!urlToRedirectAfterSubmission) {
                setIsLoading(false);
            }
            if (!(formResponse === null || formResponse === void 0 ? void 0 : formResponse.success)) {
                setFormError(true);
                setFormSubmitErrorMessage(formResponse === null || formResponse === void 0 ? void 0 : formResponse.errorMessage);
                Sentry.captureException(`Contact sales form submission failed: ${formResponse === null || formResponse === void 0 ? void 0 : formResponse.errorMessage}. Form data: ${JSON.stringify(formData)}`);
                trackEvent(CONTACT_SALES_FORM_SUBMISSION_CALL_FAILED, {
                    info1: formResponse === null || formResponse === void 0 ? void 0 : formResponse.errorMessage,
                    data: formData
                });
                return;
            }
            setHasFormSubmitted(true);
            if (urlToRedirectAfterSubmission) {
                window.location.href = urlToRedirectAfterSubmission;
            }
        });
        return function onButtonClick() {
            return _ref.apply(this, arguments);
        };
    }();
    const renderBackgroundLayers = ()=>{
        setBackgroundLayers(Math.min(backgroundLayers, MAX_BACKGROUND_LAYERS));
        const layers = Array(backgroundLayers).fill().map((element, index)=>index + 1);
        return layers.map((layer)=>/*#__PURE__*/ _jsx("div", {
                className: "form-background-layer",
                style: {
                    zIndex: -1 * layer,
                    marginTop: BACKGROUND_LAYER_MARGIN * layer,
                    marginLeft: -BACKGROUND_LAYER_MARGIN * (layer - 1),
                    opacity: 1 - 0.25 * layer
                }
            }, `bg_layer_${layer}`));
    };
    const shouldRenderPostFormSubmit = !dontRenderPostFormSubmit && hasFormSubmitted;
    return /*#__PURE__*/ _jsx(ConditionalWrapper, {
        condition: backgroundLayers,
        wrapper: (children)=>/*#__PURE__*/ _jsxs(StyledLayeredBaseForm, {
                className: "layered-base-form",
                children: [
                    renderBackgroundLayers(),
                    children
                ]
            }),
        children: /*#__PURE__*/ _jsxs(StyledBaseForm, {
            className: "base-form",
            children: [
                shouldRenderPostFormSubmit && renderPostFormSubmit(),
                !shouldRenderPostFormSubmit && renderForm()
            ]
        })
    });
};
BaseForm.defaultProps = DefaultProps;
export default withAbTests(withGenericConfig(BaseForm));
